import { CnDatahubCategoryColumn, DatahubColumnType } from './datahub-interfaces'
import { sortDatahubColumns } from './datahubOrderKey'

export type DatahubFilterOp =
  | 'eq'
  | 'neq'
  | 'in'
  | 'not_in'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'ilike'
  | 'is_null'
  | 'is_not_null'

export type DatahubFilterLogicalOp = 'and' | 'or'

export type DatahubFilterValue = string | number | boolean

export interface DatahubFilterCondition {
  field: string
  op: DatahubFilterOp
  value?: DatahubFilterValue
  values?: DatahubFilterValue[]
}

export interface DatahubFilterGroup {
  operator: DatahubFilterLogicalOp
  conditions: Array<DatahubFilterCondition | DatahubFilterGroup>
}

/**
 * Options for the operators of the filter
 * @returns The options for the operators of the filter
 */
export const DATAHUB_FILTER_OP_OPTIONS: { label: string; value: DatahubFilterOp }[] = [
  { label: 'égal à', value: 'eq' },
  { label: 'différent de', value: 'neq' },
  { label: 'dans la liste', value: 'in' },
  { label: 'pas dans la liste', value: 'not_in' },
  { label: 'supérieur à', value: 'gt' },
  { label: 'supérieur ou égal', value: 'gte' },
  { label: 'inférieur à', value: 'lt' },
  { label: 'inférieur ou égal', value: 'lte' },
  { label: 'contient', value: 'ilike' },
  { label: 'est vide', value: 'is_null' },
  { label: "n'est pas vide", value: 'is_not_null' },
]

/**
 * Operators allowed for the column types
 * @returns The operators allowed for the column types
 */
const OPS_BY_TYPE: Record<DatahubColumnType, DatahubFilterOp[]> = {
  text: ['ilike', 'is_null', 'is_not_null'],
  select: ['eq', 'neq', 'in', 'not_in', 'is_null', 'is_not_null'],
  entier: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'is_null', 'is_not_null'],
  decimal: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'is_null', 'is_not_null'],
  date: ['eq', 'gt', 'gte', 'lt', 'lte', 'is_null', 'is_not_null'],
  boolean: ['eq'],
  'multiline-text': ['ilike', 'is_null', 'is_not_null'],
  'multiple-labels': ['in', 'not_in', 'is_null', 'is_not_null'],
}

export function createEmptyFilterGroup(operator: DatahubFilterLogicalOp = 'and'): DatahubFilterGroup {
  return { operator, conditions: [] }
}

export function createEmptyCondition(field = ''): DatahubFilterCondition {
  return { field, op: 'eq', value: '' }
}

/**
 * Check if the node is a filter group (has an operator and conditions)
 * @param node - The node to check
 * @returns True if the node is a filter group, false otherwise (condition)
 */
export function isFilterGroup(
  node: DatahubFilterCondition | DatahubFilterGroup,
): node is DatahubFilterGroup {
  return 'operator' in node && Array.isArray((node as DatahubFilterGroup).conditions)
}

/**
 * Get the operators for a column type
 * @param type - The type of the column
 * @returns The operators for the column type
 */
export function opsForColumnType(type: DatahubColumnType | undefined): DatahubFilterOp[] {
  if (!type) return DATAHUB_FILTER_OP_OPTIONS.map((o) => o.value)
  return OPS_BY_TYPE[type] ?? OPS_BY_TYPE.text
}

/**
 * Check if the operator needs a value (for the 'is_null' and 'is_not_null' operators)
 * @param op - The operator
 * @returns True if the operator needs a value, false otherwise
 */
export function opNeedsValue(op: DatahubFilterOp): boolean {
  return op !== 'is_null' && op !== 'is_not_null'
}

/**
 * Check if the operator needs multiple values (for the 'in' and 'not_in' operators)
 * @param op - The operator
 * @returns True if the operator needs multiple values, false otherwise
 */
export function opNeedsMultipleValues(op: DatahubFilterOp): boolean {
  return op === 'in' || op === 'not_in'
}

/** Strip SQL ILIKE wildcards for display/editing (user enters plain text). */
export function stripIlikePattern(value: string): string {
  let s = value
  if (s.startsWith('%')) s = s.slice(1)
  if (s.endsWith('%')) s = s.slice(0, -1)
  return s
}

/** Wrap a plain search term for SQL ILIKE "contains" matching. */
export function wrapIlikePattern(value: string): string {
  const core = stripIlikePattern(value)
  if (core === '') return ''
  return `%${core}%`
}

/**
 * Parse the options of the select column or multiple-labels column
 * @param options - The options of the select column or multiple-labels column (JSON string)
 * @returns The options of the select column or multiple-labels column (array of strings)
 */
export function parseSelectOptions(options: string | null | undefined): string[] {
  if (!options?.trim()) return []
  try {
    const parsed = JSON.parse(options)
    if (!Array.isArray(parsed)) return []
    return parsed.map((item) => String(item))
  } catch {
    return []
  }
}

export function serializeSelectOptions(options: string[]): string | null {
  const normalized = options.map((item) => item.trim()).filter(Boolean)
  return normalized.length > 0 ? JSON.stringify(normalized) : null
}

/**
 * Check if the value is a plain object
 * @param value - The value to check
 * @returns True if the value is a plain object, false otherwise
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Parse the value
 * @param raw - The raw value
 * @returns The parsed value
 */
function parseValue(raw: unknown): DatahubFilterValue | undefined {
  if (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') return raw
  return undefined
}

/**
 * Parse the condition
 * @param raw - The raw condition (for example: { field: 'surface', op: 'gt', value: '100' })
 * @returns The parsed condition (as DatahubFilterCondition object)
 */
function parseCondition(raw: Record<string, unknown>): DatahubFilterCondition | null {
  if (typeof raw.field !== 'string' || typeof raw.op !== 'string') return null
  const op = raw.op as DatahubFilterOp
  const condition: DatahubFilterCondition = { field: raw.field, op }

  if (opNeedsMultipleValues(op)) {
    if (Array.isArray(raw.values)) {
      condition.values = raw.values
        .map(parseValue)
        .filter((v): v is DatahubFilterValue => v !== undefined)
    } else {
      condition.values = []
    }
  } else if (opNeedsValue(op)) {
    const rawValue = parseValue(raw.value) ?? ''
    condition.value =
      op === 'ilike' && typeof rawValue === 'string' ? stripIlikePattern(rawValue) : rawValue
  }

  return condition
}

/**
 * Parse the node
 * @param raw - The raw node (for example: { operator: 'and', conditions: [{ field: 'surface', op: 'gt', value: '100' }] })
 * @returns The parsed node (as DatahubFilterCondition or DatahubFilterGroup object)
 */
function parseNode(raw: unknown): DatahubFilterCondition | DatahubFilterGroup | null {
  if (!isPlainObject(raw)) return null
  if (typeof raw.operator === 'string' && Array.isArray(raw.conditions)) {
    const operator = raw.operator === 'or' ? 'or' : 'and'
    return {
      operator,
      conditions: raw.conditions
        .map(parseNode)
        .filter((n): n is DatahubFilterCondition | DatahubFilterGroup => n !== null),
    }
  }
  return parseCondition(raw)
}

/** Parse a stored jsonFilter string into a group. Falls back to an empty AND group. */
export function parseFilterJson(jsonFilter: string | null | undefined): DatahubFilterGroup {
  const trimmed = jsonFilter?.trim() ?? ''
  if (!trimmed || trimmed === '{}') return createEmptyFilterGroup()
  try {
    const parsed = parseNode(JSON.parse(trimmed))
    if (parsed && isFilterGroup(parsed)) return parsed
    if (parsed) return { operator: 'and', conditions: [parsed] }
  } catch {
    // ignore invalid JSON
  }
  return createEmptyFilterGroup()
}

/**
 * Serialize the condition
 * @param condition - The condition to serialize (as DatahubFilterCondition object)
 * @returns The serialized condition (as JSON object)
 */
function serializeCondition(condition: DatahubFilterCondition): Record<string, unknown> {
  const base: Record<string, unknown> = { field: condition.field, op: condition.op }
  if (!opNeedsValue(condition.op)) return base
  if (opNeedsMultipleValues(condition.op)) {
    return { ...base, values: condition.values ?? [] }
  }
  const value = condition.value ?? ''
  return {
    ...base,
    value:
      condition.op === 'ilike' && typeof value === 'string' ? wrapIlikePattern(value) : value,
  }
}

/**
 * Serialize the node
 * @param node - The node to serialize (as DatahubFilterCondition or DatahubFilterGroup object)
 * @returns The serialized node (as JSON object)
 */
function serializeNode(
  node: DatahubFilterCondition | DatahubFilterGroup,
): Record<string, unknown> {
  if (isFilterGroup(node)) {
    return {
      operator: node.operator,
      conditions: node.conditions.map(serializeNode),
    }
  }
  return serializeCondition(node)
}

/**
 * Serialize the group
 * @param group - The group to serialize (as DatahubFilterGroup object)
 * @returns The serialized group (as JSON string)
 */
export function serializeFilterGroup(group: DatahubFilterGroup): string {
  return JSON.stringify(serializeNode(group))
}

/** Expected format for the values of a 'date' column. */
export const DATAHUB_DATE_FORMAT = 'AAAA-MM-JJ'

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * Check if a YYYY-MM-DD string is a real calendar date
 * @param raw - The raw value (already matching DATE_PATTERN)
 * @returns True when the day actually exists (rejects 2024-02-31 for example)
 */
function isRealCalendarDate(raw: string): boolean {
  const match = DATE_PATTERN.exec(raw)
  if (!match) return false
  const [, year, month, day] = match.map(Number)
  if (month < 1 || month > 12) return false
  const date = new Date(Date.UTC(year, month - 1, day))
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  )
}

/**
 * Validate the format of a filter value against a column type.
 * Empty values are not reported here: completeness is handled by isConditionComplete.
 * @param value - The value to validate
 * @param type - The type of the column
 * @param op - The operator
 * @returns An error message, or null when the format is valid
 */
export function validateFilterValueFormat(
  value: DatahubFilterValue | undefined,
  type: DatahubColumnType | undefined,
  op: DatahubFilterOp,
): string | null {
  if (!opNeedsValue(op)) return null
  if (value === undefined || value === null || value === '') return null

  switch (type) {
    case 'date': {
      const raw = String(value).trim()
      if (!DATE_PATTERN.test(raw)) return `Format attendu : ${DATAHUB_DATE_FORMAT}`
      if (!isRealCalendarDate(raw)) return 'Cette date n’existe pas'
      return null
    }
    case 'entier': {
      const raw = typeof value === 'number' ? value : String(value).trim()
      const n = Number(raw)
      if (!Number.isFinite(n)) return 'Nombre entier attendu'
      if (!Number.isInteger(n)) return 'Nombre entier attendu (sans décimale)'
      return null
    }
    case 'decimal': {
      const raw = typeof value === 'number' ? String(value) : String(value).trim()
      if (raw.includes(',')) return 'Utilisez le point comme séparateur décimal'
      if (!Number.isFinite(Number(raw))) return 'Nombre attendu'
      return null
    }
    case 'text':
    case 'multiline-text':
      return String(value).trim() ? null : 'La valeur ne peut pas être uniquement des espaces'
    default:
      return null
  }
}

/**
 * Validate a filter value against a column (options for select columns, format otherwise)
 * @param value - The value to validate
 * @param column - The column
 * @param op - The operator
 * @returns An error message, or null when the value is valid
 */
export function validateFilterValueForColumn(
  value: DatahubFilterValue | undefined,
  column: CnDatahubCategoryColumn | undefined,
  op: DatahubFilterOp,
): string | null {
  if (!opNeedsValue(op)) return null
  const options = parseSelectOptions(column?.options)
  if ((column?.type === 'select' || column?.type === 'multiple-labels') && options.length > 0) {
    if (value === undefined || value === null || value === '') return null
    return options.includes(String(value)) ? null : 'Valeur absente des options de la colonne'
  }
  return validateFilterValueFormat(value, column?.type, op)
}

/**
 * Get the values that do not match the expected format of the column
 * @param values - The values to validate
 * @param column - The column
 * @param op - The operator
 * @returns The invalid values (empty array when they are all valid)
 */
export function invalidFilterValues(
  values: DatahubFilterValue[] | undefined,
  column: CnDatahubCategoryColumn | undefined,
  op: DatahubFilterOp,
): DatahubFilterValue[] {
  return (values ?? []).filter((value) => validateFilterValueForColumn(value, column, op) !== null)
}

/**
 * Check if the condition is complete
 * @param condition - The condition to check (as DatahubFilterCondition object)
 * @param columns - The columns, used to validate the value against the column type
 * @returns True if the condition is complete, false otherwise (complete = field + operator + valid value/values)
 */
function isConditionComplete(
  condition: DatahubFilterCondition,
  columns: CnDatahubCategoryColumn[],
): boolean {
  if (!condition.field.trim()) return false
  if (!opNeedsValue(condition.op)) return true
  const column = findColumnByDatakey(columns, condition.field)
  if (opNeedsMultipleValues(condition.op)) {
    if ((condition.values?.length ?? 0) === 0) return false
    return invalidFilterValues(condition.values, column, condition.op).length === 0
  }
  if (condition.value === '' || condition.value === undefined || condition.value === null) {
    return false
  }
  return validateFilterValueForColumn(condition.value, column, condition.op) === null
}

/**
 * Check if the group is complete
 * @param group - The group to check (as DatahubFilterGroup object)
 * @param columns - The columns, used to validate the values against the column types
 * @returns True if the group is complete, false otherwise (complete = at least one condition)
 */
function isGroupComplete(group: DatahubFilterGroup, columns: CnDatahubCategoryColumn[]): boolean {
  if (group.conditions.length === 0) return false
  return group.conditions.every((node) =>
    isFilterGroup(node) ? isGroupComplete(node, columns) : isConditionComplete(node, columns),
  )
}

/**
 * True when jsonFilter is valid JSON matching the expected filter shape with at least one complete condition.
 * When columns are provided, the values are also checked against the format of their column.
 */
export function isFilterJsonValid(
  jsonFilter: string | null | undefined,
  columns: CnDatahubCategoryColumn[] = [],
): boolean {
  const trimmed = jsonFilter?.trim() ?? ''
  if (!trimmed || trimmed === '{}') return false
  try {
    const parsed = parseNode(JSON.parse(trimmed))
    if (!parsed) return false
    if (isFilterGroup(parsed)) return isGroupComplete(parsed, columns)
    return isConditionComplete(parsed, columns)
  } catch {
    return false
  }
}

/**
 * Get the data for the columns (label, value, type)
 * @param columns - The columns
 * @returns The data for the columns (label, value, type)
 */
export function columnDataForDatahubCategory(
  columns: CnDatahubCategoryColumn[],
): { label: string; value: string; type: DatahubColumnType }[] {
  return sortDatahubColumns(columns.filter((col) => col.datakey.trim())).map((col) => ({
      label: col.label || col.name || col.datakey,
      value: col.datakey,
      type: col.type,
    }))
}

/**
 * Find the column by the datakey
 * @param columns - The columns
 * @param datakey - The datakey of the column
 * @returns The column (as CnDatahubCategoryColumn object)
 */
export function findColumnByDatakey(
  columns: CnDatahubCategoryColumn[],
  datakey: string,
): CnDatahubCategoryColumn | undefined {
  return columns.find((col) => col.datakey === datakey)
}
